import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, Typography, Button, Stack, Tooltip } from '@mui/material';
import { ForwardToInbox, ContentCopy } from '@mui/icons-material';
import { emailAddressApi } from '../services/api';
import { useNotification } from '../hooks/useNotification';

/**
 * First run, the easiest way to get transactions in: shown until the first
 * forwarded email arrives. Without an address it offers to set one up; with
 * one it shows the address to forward to.
 */
const ForwardingSetupCard = () => {
  const navigate = useNavigate();
  const { showSuccess } = useNotification();
  const [state, setState] = useState(null); // { address, waiting }

  useEffect(() => {
    emailAddressApi.get()
      .then(response => {
        const data = response.data || {};
        if (!data.emailAddress || !data.isActive) setState({ address: null });
        else if (!data.lastEmailReceivedAt) setState({ address: data.emailAddress });
        else setState(null);
      })
      .catch(error => setState(error.response?.status === 404 ? { address: null } : null));
  }, []);

  if (!state) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(state.address);
      showSuccess('Address copied');
    } catch (e) {
      // Clipboard blocked: the address is on screen to copy by hand
    }
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <ForwardToInbox color="primary" sx={{ fontSize: 40 }} />
          <Stack sx={{ flex: 1 }} spacing={0.5}>
            <Typography variant="h6">
              {state.address ? 'Forward your bank emails to your address' : 'Forward your bank emails'}
            </Typography>
            {state.address ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                  {state.address}
                </Typography>
                <Tooltip title="Copy address">
                  <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={copy}>Copy</Button>
                </Tooltip>
              </Stack>
            ) : null}
            <Typography variant="body2" color="text.secondary">
              {state.address
                ? 'Nothing has arrived yet. Add a Gmail filter that forwards your bank alerts and statements here; they are read automatically and wait for your review.'
                : "Get your own PinePocket address and forward your bank's alert emails to it. Transactions are read automatically and wait for your review; card statements become bills with due dates."}
            </Typography>
          </Stack>
          <Button variant="contained" onClick={() => navigate('/settings?tab=email')}>
            {state.address ? 'Setup steps' : 'Set up forwarding'}
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ForwardingSetupCard;
